import {
  consigneCreation,
  consigneModification,
  decrireCritere,
  ecrireConditionObjet,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import {
  api,
  type Consigne,
  corps,
  ErreurApi,
  type FicheTache,
  gere,
  lire,
  type PropositionConsigne as Proposition,
  requeteMoi,
  requetes,
} from '../api'
import { LIBELLES_STATUT_CONSIGNE } from '../libelles'
import {
  champsConsigne,
  type Essai,
  type Evaluation,
  LigneConsigne,
  Remarques,
  useChoix,
} from './Consignes'
import { LigneConsigneFournisseur } from './ConsignesFournisseur'
import { Formulaire } from './Formulaire'
import { Icone } from './Icone'

// En haut de la tâche : la consigne de tri qui a trié son mail, et pour chaque pièce la
// consigne de fournisseur qui l'a rattachée. Chacune s'ouvre sur place (modifier, activer,
// essai à blanc). Un bouton demande à Claude la consigne qui trierait les prochains mails
// semblables : rien n'est écrit avant « Créer et activer », après relecture.

type Panneau =
  | { etape: 'ferme' }
  | { etape: 'lecture' }
  | {
      etape: 'ouvert'
      p: Proposition
      verification: { evaluation: Evaluation; essai: Essai } | null
      /** La consigne qui a trié ce mail : la proposition la corrige au lieu d'en créer une. */
      existante: Consigne | null
      /** Change à chaque proposition : le formulaire repart de ses valeurs. */
      version: number
    }
  | { etape: 'cree'; id: string; evaluation: Evaluation | null }

export function ConsignesTache({ t, ecriture }: { t: FicheTache; ecriture: boolean }) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const [ouverte, setOuverte] = useState<string | null>(null)
  const [panneau, setPanneau] = useState<Panneau>({ etape: 'ferme' })
  const [demande, setDemande] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  // Les consignes complètes ne se chargent que pour les ouvrir ou les corriger.
  const besoin = ouverte !== null || panneau.etape !== 'ferme'
  const tri = useQuery({ ...requetes.consignes, enabled: besoin })
  const fournisseur = useQuery({ ...requetes.consignesFournisseur, enabled: besoin })
  const entites = useQuery(requetes.entites).data ?? []
  const { consignes } = t
  const peutProposer = ecriture && t.mails.length > 0
  if (t.mails.length === 0 && consignes.fournisseur.length === 0) return null

  const gerees = entites
    .filter((e) => !e.archivee && moi && gere(moi, e.id))
    .map((e) => ({ valeur: e.id, libelle: e.nom }))
  const modifiable = (c: Consigne) =>
    c.entiteId === null ? Boolean(moi?.administrateur) : gerees.some((e) => e.valeur === c.entiteId)
  const nomPiece = (id: string) => t.pieces.find((p) => p.id === id)?.nomFichier ?? 'Pièce'

  async function proposer(precision: string | null) {
    setErreur(null)
    setPanneau({ etape: 'lecture' })
    try {
      const p = await lire(
        api.taches[':id']['proposer-consigne'].$post({
          param: { id: t.id },
          json: precision ? { demande: precision } : {},
        }),
      )
      // Le mail a déjà une consigne : lue avant d'ouvrir, pour la corriger sans la doubler.
      const appliquee = consignes.tri[0]
      const existante = appliquee
        ? ((await client.fetchQuery(requetes.consignes)).find((c) => c.id === appliquee.id) ?? null)
        : null
      // Grille et essai à blanc tout de suite : ce que la consigne déclencherait. Une
      // proposition incomplète s'ouvre quand même, le formulaire dira ce qui manque.
      const lue = consigneCreation.safeParse(p.proposition)
      const verification = lue.success
        ? await lire(api.consignes.verifier.$post({ json: lue.data })).catch(() => null)
        : null
      setPanneau({ etape: 'ouvert', p, verification, existante, version: Date.now() })
    } catch (e) {
      setPanneau({ etape: 'ferme' })
      setErreur(e instanceof ErreurApi ? e.message : 'Proposition impossible.')
    }
  }

  return (
    <section className="bandeau-consigne">
      <div className="bandeau-consigne-ligne">
        <span className="tuile-icone petite">
          <Icone nom="rule" />
        </span>
        <div className="bandeau-consigne-texte">
          {consignes.tri.map((c) => (
            <span key={c.id}>
              {c.ignorer ? 'Consigne qui ignore' : 'Trié par la consigne'}{' '}
              <button
                type="button"
                className="bouton-texte"
                aria-expanded={ouverte === c.id}
                onClick={() => setOuverte(ouverte === c.id ? null : c.id)}
              >
                « {c.sujet} »
              </button>
              <span className="discret"> · {c.entite ?? 'toutes entités'}</span>
              {c.statut !== 'active' && (
                <span className="badge">{LIBELLES_STATUT_CONSIGNE[c.statut]}</span>
              )}
            </span>
          ))}
          {t.mails.length > 0 && consignes.tri.length === 0 && (
            <span>
              <strong>Aucune consigne de tri</strong>
              <span className="discret"> : ce mail a été trié par Claude seul.</span>
            </span>
          )}
          {consignes.fournisseur.map((c) => (
            <span key={c.pieceId}>
              {nomPiece(c.pieceId)} rattachée par la consigne{' '}
              <button
                type="button"
                className="bouton-texte"
                aria-expanded={ouverte === c.id}
                onClick={() => setOuverte(ouverte === c.id ? null : c.id)}
              >
                {c.tiers} : {c.criteres.map(decrireCritere).join(' ou ') || c.texte}
              </button>
              <span className="discret"> → {c.entite}</span>
              {c.statut !== 'active' && (
                <span className="badge">{LIBELLES_STATUT_CONSIGNE[c.statut]}</span>
              )}
            </span>
          ))}
        </div>
        {peutProposer && panneau.etape === 'ferme' && (
          <button type="button" className="bouton" onClick={() => void proposer(null)}>
            <Icone nom="auto_awesome" taille={18} />
            {consignes.tri.length > 0 ? 'Corriger avec Claude' : 'Proposer une consigne'}
          </button>
        )}
      </div>
      {erreur && <p className="alerte">{erreur}</p>}

      {ouverte &&
        (() => {
          const c = tri.data?.find((x) => x.id === ouverte)
          if (c) return <LigneConsigne c={c} modifiable={modifiable(c)} />
          const f = fournisseur.data?.find((x) => x.id === ouverte)
          if (f) return <LigneConsigneFournisseur c={f} gerees={gerees} avecTiers />
          return <p className="discret">Chargement…</p>
        })()}

      {panneau.etape === 'lecture' && <p className="discret">Claude lit la tâche et son mail…</p>}
      {panneau.etape === 'ouvert' && (
        <PanneauProposition
          key={panneau.version}
          p={panneau.p}
          verification={panneau.verification}
          existante={panneau.existante}
          gerees={gerees}
          administrateur={Boolean(moi?.administrateur)}
          demande={demande}
          onDemande={setDemande}
          onReproposer={() => void proposer(demande.trim() || null)}
          onAnnuler={() => setPanneau({ etape: 'ferme' })}
          onFait={async (id, evaluation) => {
            await client.invalidateQueries()
            setDemande('')
            setPanneau({ etape: 'cree', id, evaluation })
          }}
        />
      )}
      {panneau.etape === 'cree' && (
        <div className="pile">
          <p>
            <Icone nom="check" taille={18} /> Consigne enregistrée et active : elle triera les
            prochains mails. Pour ce mail et les autres tâches ouvertes qu’elle déclenche : «
            Appliquer aux tâches ouvertes ».
          </p>
          <Remarques e={panneau.evaluation} />
          {(() => {
            const c = tri.data?.find((x) => x.id === panneau.id)
            return c ? <LigneConsigne c={c} modifiable={modifiable(c)} /> : null
          })()}
          <div className="boutons">
            <button
              type="button"
              className="bouton-discret"
              onClick={() => setPanneau({ etape: 'ferme' })}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * La proposition de Claude, dans le formulaire habituel des consignes : on la retouche, puis
 * on la crée (active), ou l'on corrige la consigne qui a trié ce mail. Une précision écrite
 * relance la proposition.
 */
function PanneauProposition({
  p,
  verification,
  existante,
  gerees,
  administrateur,
  demande,
  onDemande,
  onReproposer,
  onAnnuler,
  onFait,
}: {
  p: Proposition
  verification: { evaluation: Evaluation; essai: Essai } | null
  existante: Consigne | null
  gerees: { valeur: string; libelle: string }[]
  administrateur: boolean
  demande: string
  onDemande: (v: string) => void
  onReproposer: () => void
  onAnnuler: () => void
  onFait: (id: string, evaluation: Evaluation | null) => Promise<void>
}) {
  const choix = useChoix()
  const idPrecision = useId()
  const initial = { ...p.proposition, objets: p.proposition.objets.map(ecrireConditionObjet) }
  const autreEntite =
    existante !== null && p.proposition.entiteId !== existante.entiteId
      ? (gerees.find((e) => e.valeur === p.proposition.entiteId)?.libelle ?? 'toutes entités')
      : null
  const essai = verification?.essai
  return (
    <div className="carte proposition-consigne">
      <h3 className="titre-icone">
        <Icone nom="auto_awesome" />
        {existante ? `Corriger « ${existante.sujet} »` : 'Consigne proposée par Claude'}
      </h3>
      {p.explication && <p>{p.explication}</p>}
      {p.mailsIlisibles > 0 && (
        <p className="discret">
          Mail d’origine illisible (boîte déconnectée ?) : proposée d’après la tâche seule.
        </p>
      )}
      {autreEntite && (
        <p className="alerte">
          Claude la verrait sur {autreEntite}. L’entité d’une consigne ne change pas : la
          désactiver, puis en créer une autre depuis Réglages.
        </p>
      )}
      {verification && <Remarques e={verification.evaluation} />}
      {essai && (
        <p className="discret">
          Essai à blanc : {essai.declenches} mail(s) sur {essai.mailsLus} reçus en 90 jours
          {essai.changeraient > 0 &&
            `, dont ${essai.changeraient} classé(s) aujourd’hui ailleurs`}. {essai.note}
        </p>
      )}
      <Formulaire
        champs={champsConsigne(
          choix,
          existante ? null : { entites: gerees, transverse: administrateur },
        )}
        initial={initial}
        libelleBouton={existante ? 'Enregistrer et activer' : 'Créer et activer'}
        onAnnuler={onAnnuler}
        onValider={async (v) => {
          if (existante) {
            await lire(
              api.consignes[':id'].$patch({
                param: { id: existante.id },
                json: corps(consigneModification, v),
              }),
            )
            if (existante.statut !== 'active')
              await lire(
                api.consignes[':id'].action.$post({
                  param: { id: existante.id },
                  json: { action: 'activer' },
                }),
              )
            await onFait(existante.id, null)
            return
          }
          // Écrite par un humain dans l'appli : active d'emblée.
          const r = await lire(api.consignes.$post({ json: corps(consigneCreation, v) }))
          await onFait(r.consigne.id, r.evaluation)
        }}
      />
      <div className="reproposer">
        <div className="champ">
          <label htmlFor={idPrecision}>Préciser pour Claude</label>
          <input
            id={idPrecision}
            type="text"
            value={demande}
            maxLength={1000}
            placeholder="Toujours pour la SC, sauf les appels de fonds…"
            onChange={(e) => onDemande(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="bouton-discret"
          disabled={!demande.trim()}
          onClick={onReproposer}
        >
          <Icone nom="restart_alt" taille={18} />
          Reproposer
        </button>
      </div>
    </div>
  )
}
